import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';
import { DateTimePicker } from '@/components/DateTimePicker';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

// ============================================================================
// External Declarations
// ============================================================================

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import moment from 'moment-timezone';
import { AccessAuthorities } from '@/models';
import { getOfficerLabel } from '@/utils/legacy-utils';
import { listHasOtherCompanyItems } from '@/utils/call-card-helpers';
import Papa from 'papaparse';
import { DATETIME_FORMAT_SERVER_UTC, DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT, notifyApp, isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp } from '@/utils/window-globals';

import { getOfficerPluralLabel } from '@/utils/auth';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getModuleTypeInfo = _wg('getModuleTypeInfo');
const displayNocValuesForGridTemplate = _wg('displayNocValuesForGridTemplate');
const getFormattedDisposition = _wg('getFormattedDisposition');
const getAllPolygonLabelsAndIds = _wg('getAllPolygonLabelsAndIds');
import { b64toBlob } from '@/utils/blob';
import { saveAs } from 'file-saver';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;
const SHOW_TIMEZONE_FILTER = false;

// ============================================================================
// Column Definitions
// ============================================================================

function getCallCardColumns(): KstDataGridColumn[] {
  return [
    { field: 'clientName', name: 'Client', template: (e: any) => e.clientName || '' },
    { field: 'clientSiteNames', name: 'Sites', template: (e: any) => e.clientSiteNames ? e.clientSiteNames.join(', ') : '' },
    { field: 'eventId', name: 'Event Id' },
    { field: 'callDateTime', name: 'Date/Time', template: (e: any) => e.callDateTime ? moment(e.callDateTime).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
    { field: 'natureOfCallValues', name: 'Nature Of Call', template: (e: any) => displayNocValuesForGridTemplate(e) },
    { field: 'primaryOfficerName', name: `Primary ${getOfficerLabel()}`, template: (e: any) => e.primaryOfficerName || '' },
    { field: 'backupUnits', name: `Backup ${getOfficerPluralLabel()}`, template: (e: any) => e.backupUnits ? e.backupUnits.map((u: any) => u.name).join(', ') : '' },
    { field: 'callLoc', name: 'Event Location' },
    { field: 'dispositionNotes', name: 'Disposition', template: (e: any) => getFormattedDisposition(e.callDispositionValue, e.dispositionNotes) },
    { field: 'createdByName', name: 'Created By', template: (e: any) => e.createdByName || '' },
    { field: 'primaryDispatched', name: 'Primary Dispatched', template: (e: any) => e.primaryDispatched ? moment(e.primaryDispatched).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
    { field: 'primaryEnroute', name: 'Primary Enroute', template: (e: any) => e.primaryEnroute ? moment(e.primaryEnroute).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
    { field: 'primaryOnScene', name: 'Primary On Scene', template: (e: any) => e.primaryOnScene ? moment(e.primaryOnScene).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
    { field: 'primaryCleared', name: 'Primary Cleared', template: (e: any) => e.primaryCleared ? moment(e.primaryCleared).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
  ];
}

function getReportColumns(): KstDataGridColumn[] {
  return [
    { field: 'clientNames', name: 'Clients', template: (e: any) => e.clientNames ? e.clientNames.join(', ') : '' },
    { field: 'clientSiteNames', name: 'Sites', template: (e: any) => e.clientSiteNames ? e.clientSiteNames.join(', ') : '' },
    { field: 'caseId', name: 'Case Id' },
    { field: 'dateCreated', name: 'Date/Time', template: (e: any) => e.dateCreated ? moment(e.dateCreated).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
    { field: 'incidentOccurredFrom', name: 'Occurred', template: (e: any) => e.incidentOccurredFrom ? moment(e.incidentOccurredFrom).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
    { field: 'natureOfCallValues', name: 'Incident Nature', template: (e: any) => displayNocValuesForGridTemplate(e) },
    { field: 'callLoc', name: 'Address' },
    { field: 'authorName', name: 'Author' },
    { field: 'incidentDescription', name: 'Brief Description', template: (e: any) => e.incidentDescription || '' },
  ];
}

function getNotesColumns(): KstDataGridColumn[] {
  return [
    { field: 'clientName', name: 'Client', template: (e: any) => e.clientName || '' },
    { field: 'clientSiteNames', name: 'Sites', template: (e: any) => e.clientSiteNames ? e.clientSiteNames.join(', ') : '' },
    { field: 'dateTime', name: 'Date/Time', template: (e: any) => e.dateTime ? moment(e.dateTime).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
    { field: 'onBehalfOfName', name: 'On Behalf Of' },
    { field: 'loggedByName', name: 'Logged By' },
    { field: 'subject', name: 'Subject' },
    { field: 'location', name: 'Location' },
    { field: 'notes', name: 'Notes' },
  ];
}

function getTourStopsColumns(): KstDataGridColumn[] {
  return [
    { field: 'clientName', name: 'Client', template: (e: any) => e.clientName || '' },
    { field: 'clientSiteNames', name: 'Sites', template: (e: any) => e.clientSiteNames ? e.clientSiteNames.join(', ') : '' },
    { field: 'location', name: 'Location' },
    { field: 'officerName', name: `${getOfficerLabel()} Name` },
    { field: 'completedAt', name: 'Completed At', template: (e: any) => e.completedAt ? moment(e.completedAt).format(DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '' },
    { field: 'officerComments', name: 'Comments' },
    { field: 'tourStopIssueData', name: 'Issues', template: (e: any) => formatTourStopIssues(e.tourStopIssueData) },
    { field: 'latitude', name: 'Latitude' },
    { field: 'longitude', name: 'Longitude' },
  ];
}

function formatTourStopIssues(issues: any[]): string {
  if (!issues || issues.length === 0) return '';
  return issues.map((issue: any) => {
    const status = issue.isResolved ? '[Resolved]' : '[Open]';
    return `${status} ${issue.title}`;
  }).join('; ');
}

// ============================================================================
// CSV Download Utility
// ============================================================================

function downloadCsvFromData(allData: any[], columns: KstDataGridColumn[], filename: string): void {
  const fields = columns.map((c) => c.name);
  const data = allData.map((row) =>
    columns.map((col) => {
      if (col.template) return col.template(row);
      return row[col.field] ?? '';
    })
  );

  const csv: string = Papa.unparse({ fields, data });

  if (isUWPApp || isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
    App.showPopupNotification('Not available on apps. Must download from web (https://orna.kerkton.com).', 'error');
  } else {
    const blob = new Blob([csv], { type: 'text/csv' });
    saveAs(blob, filename);
  }
}

// ============================================================================
// Pagination Utility
// ============================================================================

function getPageData(allData: any[], currentPage: number, pageSize: number): { pageData: any[]; pager: KstDataGridPager } {
  const totalRecords = allData.length;
  const numPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRecords);
  const pageData = allData.slice(startIndex, endIndex);

  return {
    pageData,
    pager: {
      curPage: currentPage,
      numPages,
      records: totalRecords,
      isVisible: totalRecords > pageSize,
    },
  };
}

// ============================================================================
// ActivityReportScreen Component
// ============================================================================

export function ActivityReportScreen() {
  const { theme } = useTheme();

  // --- Labels ---
  const officerLabel = useMemo(() => getOfficerLabel(), []);
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  // --- Date Range ---
  const [fromDate, setFromDate] = useState<Date | null>(new Date());
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [isFromDatePickerOpen, setIsFromDatePickerOpen] = useState(false);
  const [isToDatePickerOpen, setIsToDatePickerOpen] = useState(false);

  // --- Filter Data Sources ---
  const [clientList, setClientList] = useState<any[]>([]);
  const [clientSiteList, setClientSiteList] = useState<any[]>([]);
  const [officerList, setOfficerList] = useState<any[]>([]);
  const [natureOfCallList, setNatureOfCallList] = useState<any[]>([]);
  const [dispositionList, setDispositionList] = useState<any[]>([]);
  const [mapAreaList, setMapAreaList] = useState<any[]>([]);

  // --- Selected Filter Values ---
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedClientSiteIds, setSelectedClientSiteIds] = useState<number[]>([]);
  const [selectedOfficerId, setSelectedOfficerId] = useState<number | null>(null);
  const [selectedNatureOfCallCodes, setSelectedNatureOfCallCodes] = useState<string[]>([]);
  const [selectedDispositionCodes, setSelectedDispositionCodes] = useState<number[]>([]);
  const [selectedMapAreaIds, setSelectedMapAreaIds] = useState<number[]>([]);

  // --- Include Checkboxes ---
  const [includeCalls, setIncludeCalls] = useState(true);
  const [includeCaseReports, setIncludeCaseReports] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(true);
  const [includeTourStops, setIncludeTourStops] = useState(true);
  const [includeInternalNotes, setIncludeInternalNotes] = useState(true);
  const [internalNotesEnabled, setInternalNotesEnabled] = useState(true);
  const [showMileageButton, setShowMileageButton] = useState(true);
  const [hasResults, setHasResults] = useState(false);

  // --- Time Zone (companyId 2 only) ---
  const showTimeZoneFilter = SHOW_TIMEZONE_FILTER;
  const [timeZones, setTimeZones] = useState<any[]>([]);
  const [selectedTimeZoneId, setSelectedTimeZoneId] = useState<string>(moment.tz.guess());

  // --- All Data (for pagination) ---
  const [allCallCardData, setAllCallCardData] = useState<any[]>([]);
  const [allReportData, setAllReportData] = useState<any[]>([]);
  const [allNotesData, setAllNotesData] = useState<any[]>([]);
  const [allTourStopsData, setAllTourStopsData] = useState<any[]>([]);

  // --- Grid State ---
  const [callCardGridRows, setCallCardGridRows] = useState<KstDataGridRows>({ entries: [] });
  const [callCardGridPager, setCallCardGridPager] = useState<KstDataGridPager>({ curPage: 1, numPages: 1, records: 0, isVisible: false });
  const [callCardCurrentPage, setCallCardCurrentPage] = useState(1);

  const [reportGridRows, setReportGridRows] = useState<KstDataGridRows>({ entries: [] });
  const [reportGridPager, setReportGridPager] = useState<KstDataGridPager>({ curPage: 1, numPages: 1, records: 0, isVisible: false });
  const [reportCurrentPage, setReportCurrentPage] = useState(1);

  const [notesGridRows, setNotesGridRows] = useState<KstDataGridRows>({ entries: [] });
  const [notesGridPager, setNotesGridPager] = useState<KstDataGridPager>({ curPage: 1, numPages: 1, records: 0, isVisible: false });
  const [notesCurrentPage, setNotesCurrentPage] = useState(1);

  const [tourStopsGridRows, setTourStopsGridRows] = useState<KstDataGridRows>({ entries: [] });
  const [tourStopsGridPager, setTourStopsGridPager] = useState<KstDataGridPager>({ curPage: 1, numPages: 1, records: 0, isVisible: false });
  const [tourStopsCurrentPage, setTourStopsCurrentPage] = useState(1);

  // --- Loading State ---
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);

  // --- Refs for stable callbacks ---
  const fromDateRef = useRef(fromDate);
  fromDateRef.current = fromDate;
  const toDateRef = useRef(toDate);
  toDateRef.current = toDate;
  const selectedClientIdRef = useRef(selectedClientId);
  selectedClientIdRef.current = selectedClientId;
  const selectedClientSiteIdsRef = useRef(selectedClientSiteIds);
  selectedClientSiteIdsRef.current = selectedClientSiteIds;
  const selectedOfficerIdRef = useRef(selectedOfficerId);
  selectedOfficerIdRef.current = selectedOfficerId;
  const selectedNatureOfCallCodesRef = useRef(selectedNatureOfCallCodes);
  selectedNatureOfCallCodesRef.current = selectedNatureOfCallCodes;
  const selectedDispositionCodesRef = useRef(selectedDispositionCodes);
  selectedDispositionCodesRef.current = selectedDispositionCodes;
  const selectedMapAreaIdsRef = useRef(selectedMapAreaIds);
  selectedMapAreaIdsRef.current = selectedMapAreaIds;
  const includeCallsRef = useRef(includeCalls);
  includeCallsRef.current = includeCalls;
  const includeCaseReportsRef = useRef(includeCaseReports);
  includeCaseReportsRef.current = includeCaseReports;
  const includeNotesRef = useRef(includeNotes);
  includeNotesRef.current = includeNotes;
  const includeTourStopsRef = useRef(includeTourStops);
  includeTourStopsRef.current = includeTourStops;
  const includeInternalNotesRef = useRef(includeInternalNotes);
  includeInternalNotesRef.current = includeInternalNotes;
  const selectedTimeZoneIdRef = useRef(selectedTimeZoneId);
  selectedTimeZoneIdRef.current = selectedTimeZoneId;

  const allCallCardDataRef = useRef(allCallCardData);
  allCallCardDataRef.current = allCallCardData;
  const allReportDataRef = useRef(allReportData);
  allReportDataRef.current = allReportData;
  const allNotesDataRef = useRef(allNotesData);
  allNotesDataRef.current = allNotesData;
  const allTourStopsDataRef = useRef(allTourStopsData);
  allTourStopsDataRef.current = allTourStopsData;

  // --- Column Definitions (memoized) ---
  const callCardGridColumns = useMemo(() => getCallCardColumns(), []);
  const reportGridColumns = useMemo(() => getReportColumns(), []);
  const notesGridColumns = useMemo(() => getNotesColumns(), []);
  const tourStopsGridColumns = useMemo(() => getTourStopsColumns(), []);

  // --- Disposition filter active flag ---
  const hasDispositionFilter = selectedDispositionCodes.length > 0;

  // --- Build Report Data ---
  const getReportDataPayload = useCallback(() => {
    const fromDateValue = fromDateRef.current;
    const toDateValue = toDateRef.current;
    const fromDateStr: string | null = fromDateValue
      ? moment.tz(moment(fromDateValue).format(), 'UTC').format(DATETIME_FORMAT_SERVER_UTC)
      : null;
    const toDateStr: string | null = toDateValue
      ? moment.tz(moment(toDateValue).format(), 'UTC').format(DATETIME_FORMAT_SERVER_UTC)
      : null;

    const clientId = selectedClientIdRef.current;
    const clientSiteIds = selectedClientSiteIdsRef.current || [];

    const dispositionCodes = selectedDispositionCodesRef.current;

    return {
      fromDate: fromDateStr,
      toDate: toDateStr,
      timeZoneId: selectedTimeZoneIdRef.current,
      client: clientId ? { id: clientId, sites: clientSiteIds } : null,
      primaryOfficerId: selectedOfficerIdRef.current,
      natureOfCallCodes: selectedNatureOfCallCodesRef.current,
      dispositionCodes: dispositionCodes.length > 0 ? dispositionCodes : null,
      mapPolygonIds: selectedMapAreaIdsRef.current,
      includeCallCards: includeCallsRef.current,
      includeCaseReports: includeCaseReportsRef.current,
      includeNotes: includeNotesRef.current,
      includeTourStops: includeTourStopsRef.current,
      includeInternalNotes: includeInternalNotesRef.current,
    };
  }, []);

  // --- Internal Notes Logic ---
  const updateInternalNotesState = useCallback((isNotesIncluded: boolean, isClientSelected: boolean) => {
    if (!isNotesIncluded || isClientSelected) {
      setIncludeInternalNotes(false);
      setInternalNotesEnabled(false);
    } else {
      setInternalNotesEnabled(true);
    }
  }, []);

  // --- Client Change Handler ---
  const onClientChange = useCallback((item: any) => {
    setSelectedClientSiteIds([]);
    setClientSiteList([]);

    if (item && item.id) {
      // Client selected - disable internal notes
      setIncludeInternalNotes(false);
      setInternalNotesEnabled(false);

      api.listClientGroupSite(item.id, (result: any) => {
        setClientSiteList(result || []);
      }, () => {});
    } else {
      // No client selected - update internal notes based on include notes checkbox
      updateInternalNotesState(includeNotesRef.current, false);
    }
  }, [updateInternalNotesState]);

  const onClientIdChange = useCallback((value: string | number | null) => {
    setSelectedClientId(value as number | null);
  }, []);

  // --- Notes Checkbox Change Handler ---
  const handleNotesCheckboxChange = useCallback((checked: boolean) => {
    setIncludeNotes(checked);
    updateInternalNotesState(checked, selectedClientIdRef.current != null);
  }, [updateInternalNotesState]);

  // --- Date Picker Handlers ---
  const closeDatePickers = useCallback(() => {
    setIsFromDatePickerOpen(false);
    setIsToDatePickerOpen(false);
  }, []);

  const openFromDatePicker = useCallback(() => {
    setIsToDatePickerOpen(false);
    setTimeout(() => setIsFromDatePickerOpen(true), 150);
  }, []);

  const openToDatePicker = useCallback(() => {
    setIsFromDatePickerOpen(false);
    setTimeout(() => setIsToDatePickerOpen(true), 150);
  }, []);

  // --- View Report ---
  const viewReport = useCallback(() => {
    closeDatePickers();
    // Use setTimeout to let date pickers commit values
    setTimeout(() => {
      // Clear previous results before fetching new data
      setAllCallCardData([]);
      setAllReportData([]);
      setAllNotesData([]);
      setAllTourStopsData([]);
      setCallCardGridRows({ entries: [] });
      setReportGridRows({ entries: [] });
      setNotesGridRows({ entries: [] });
      setTourStopsGridRows({ entries: [] });
      setCallCardGridPager({ curPage: 1, numPages: 1, records: 0, isVisible: false });
      setReportGridPager({ curPage: 1, numPages: 1, records: 0, isVisible: false });
      setNotesGridPager({ curPage: 1, numPages: 1, records: 0, isVisible: false });
      setTourStopsGridPager({ curPage: 1, numPages: 1, records: 0, isVisible: false });
      setCallCardCurrentPage(1);
      setReportCurrentPage(1);
      setNotesCurrentPage(1);
      setTourStopsCurrentPage(1);

      setIsReportLoading(true);
      const reportData = getReportDataPayload();
      api.getActivityReportData(reportData, (results: any) => {
        setHasResults(true);

        const newCallCardData = results.callCardData || [];
        const newReportData = results.reportData || [];
        const newNotesData = results.noteData || [];
        const newTourStopsData = results.guardTourStopData || [];

        setAllCallCardData(newCallCardData);
        setAllReportData(newReportData);
        setAllNotesData(newNotesData);
        setAllTourStopsData(newTourStopsData);

        // Reset to page 1
        setCallCardCurrentPage(1);
        setReportCurrentPage(1);
        setNotesCurrentPage(1);
        setTourStopsCurrentPage(1);

        // Update grids
        const cc = getPageData(newCallCardData, 1, PAGE_SIZE);
        setCallCardGridRows({ entries: cc.pageData });
        setCallCardGridPager(cc.pager);

        const rp = getPageData(newReportData, 1, PAGE_SIZE);
        setReportGridRows({ entries: rp.pageData });
        setReportGridPager(rp.pager);

        const nt = getPageData(newNotesData, 1, PAGE_SIZE);
        setNotesGridRows({ entries: nt.pageData });
        setNotesGridPager(nt.pager);

        const ts = getPageData(newTourStopsData, 1, PAGE_SIZE);
        setTourStopsGridRows({ entries: ts.pageData });
        setTourStopsGridPager(ts.pager);

        setIsReportLoading(false);
      }, () => {
        App.showPopupNotification('Error getting activity report data.', 'error');
        setIsReportLoading(false);
      });
    }, 0);
  }, [closeDatePickers, getReportDataPayload]);

  // --- Pagination ---
  const callCardGoToPage = useCallback((page: number) => {
    setCallCardCurrentPage(page);
    const result = getPageData(allCallCardDataRef.current, page, PAGE_SIZE);
    setCallCardGridRows({ entries: result.pageData });
    setCallCardGridPager(result.pager);
  }, []);

  const reportGoToPage = useCallback((page: number) => {
    setReportCurrentPage(page);
    const result = getPageData(allReportDataRef.current, page, PAGE_SIZE);
    setReportGridRows({ entries: result.pageData });
    setReportGridPager(result.pager);
  }, []);

  const notesGoToPage = useCallback((page: number) => {
    setNotesCurrentPage(page);
    const result = getPageData(allNotesDataRef.current, page, PAGE_SIZE);
    setNotesGridRows({ entries: result.pageData });
    setNotesGridPager(result.pager);
  }, []);

  const tourStopsGoToPage = useCallback((page: number) => {
    setTourStopsCurrentPage(page);
    const result = getPageData(allTourStopsDataRef.current, page, PAGE_SIZE);
    setTourStopsGridRows({ entries: result.pageData });
    setTourStopsGridPager(result.pager);
  }, []);

  // --- Download PDF ---
  const downloadPdf = useCallback(() => {
    closeDatePickers();
    setTimeout(() => {
      const reportData = getReportDataPayload();
      api.getActivityReportPDFBase64(reportData, (result: any) => {
        const pdfBase64String: string = result.pdfBase64;
        const filename: string = result.pdfFilename;
        if (isUWPApp || isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
          notifyApp('activityReportPDFBase64', [filename, pdfBase64String]);
        } else {
          const blob = b64toBlob(pdfBase64String, 'application/pdf');
          saveAs(blob, filename);
        }
      });
    }, 0);
  }, [closeDatePickers, getReportDataPayload]);

  // --- Download Mileage ---
  const downloadMileage = useCallback(() => {
    closeDatePickers();
    setTimeout(() => {
      const reportData = getReportDataPayload();
      api.getMileageActivityReportData(reportData, (result: any) => {
        const pdfBase64String: string = result.spreadsheetBase64;
        const filename: string = result.spreadsheetFilename;
        if (isUWPApp || isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
          notifyApp('activityReportPDFBase64', [filename, pdfBase64String]);
        } else {
          const blob = b64toBlob(pdfBase64String, 'application/pdf');
          saveAs(blob, filename);
        }
      });
    }, 0);
  }, [closeDatePickers, getReportDataPayload]);

  // --- Download CSV ---
  const downloadCallCardCsv = useCallback(() => {
    downloadCsvFromData(allCallCardDataRef.current, getCallCardColumns(), 'Orna Calls For Service.csv');
  }, []);

  const downloadCaseReportCsv = useCallback(() => {
    downloadCsvFromData(allReportDataRef.current, getReportColumns(), 'Orna Case Report.csv');
  }, []);

  const downloadNotesCsv = useCallback(() => {
    downloadCsvFromData(allNotesDataRef.current, getNotesColumns(), 'Orna Notes.csv');
  }, []);

  const downloadTourStopsCsv = useCallback(() => {
    downloadCsvFromData(allTourStopsDataRef.current, getTourStopsColumns(), 'Orna Tour Stops.csv');
  }, []);

  // --- Initial Load ---
  useEffect(() => {
    // Check permissions
    const perms = getModuleTypeInfo().submodules;
    if (!perms.some((n: string) => n === AccessAuthorities.ROLE_MILEAGE_DATA_EXPORT)) {
      setShowMileageButton(false);
    }

    // Load timezones
    if (SHOW_TIMEZONE_FILTER) {
      const stz = (window as any).SelectableTimeZones;
      if (typeof stz !== 'undefined') {
        setTimeZones(stz);
      }
    }

    // Load clients
    api.listClientGroup((result: any) => {
      const sorted = (result.npsdClientList || []).slice().sort((a: any, b: any) =>
        (a.name || '').localeCompare(b.name || '')
      );
      setClientList(sorted);
    });

    // Load officers
    api.getAvailableMDTOfficers((result: any) => {
      for (let i = 0; i < result.officerList.length; i++) {
        const officer = result.officerList[i];
        officer['FullName'] = officer.firstName + ' ' + officer.lastName;
      }
      result.officerList.sort((a: any, b: any) => (a.FullName > b.FullName) ? 1 : -1);
      setOfficerList(result.officerList);
    });

    // Load nature of call
    api.listNatureOfCall((result: any) => {
      setNatureOfCallList(result.natureOfCallList || []);
    }, () => {
      App.showPopupNotification('Error getting nature of call data.', 'error');
    }, true);

    // Load disposition codes
    api.listCallDispositions((result: any) => {
      setDispositionList(result.callDispositionList || []);
    }, () => {});

    // Load map areas
    getAllPolygonLabelsAndIds((result: any) => {
      const sorted = (result || []).slice().sort((a: any, b: any) =>
        (a.label || '').localeCompare(b.label || '')
      );
      setMapAreaList(sorted);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- SVG for Download Button ---
  const downloadSvg = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );

  // --- Render ---
  return (
    <div id="ActivityReportSection" className={theme}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            {/* Filter Panel */}
            <div className="kst-filter-panel">
              <div className="kst-filter-panel-header">Activity Report</div>
              <div className="kst-filter-grid">
                {/* Date Range */}
                <div className="kst-filter-group">
                  <label className="kst-filter-label">From</label>
                  <DateTimePicker
                    value={fromDate}
                    type="datetime"
                    format="DD MMM YYYY HH:mm"
                    appendToBody={true}
                    open={isFromDatePickerOpen}
                    popupClass={theme}
                    onClose={closeDatePickers}
                    onFocus={openFromDatePicker}
                    onChange={setFromDate}
                    onOpenChange={setIsFromDatePickerOpen}
                  />
                </div>
                <div className="kst-filter-group">
                  <label className="kst-filter-label">To</label>
                  <DateTimePicker
                    value={toDate}
                    type="datetime"
                    format="DD MMM YYYY HH:mm"
                    appendToBody={true}
                    open={isToDatePickerOpen}
                    popupClass={theme}
                    onClose={closeDatePickers}
                    onFocus={openToDatePicker}
                    onChange={setToDate}
                    onOpenChange={setIsToDatePickerOpen}
                  />
                </div>

                {/* Client & Sites */}
                <div className="kst-filter-group">
                  <label className="kst-filter-label">Client</label>
                  <Dropdownlist
                    value={selectedClientId}
                    items={clientList}
                    labelKey="name"
                    valueKey="id"
                    placeholder="Company-Wide"
                    clearable={true}
                    onChange={onClientIdChange}
                    onItemChange={onClientChange}
                    onOpen={closeDatePickers}
                  />
                </div>
                <div className="kst-filter-group">
                  <label className="kst-filter-label">Sites</label>
                  <MultiSelect
                    value={selectedClientSiteIds}
                    items={clientSiteList}
                    labelKey="siteName"
                    valueKey="id"
                    placeholder="Select Sites..."
                    disabled={!selectedClientId}
                    onChange={setSelectedClientSiteIds}
                    onOpen={closeDatePickers}
                  />
                </div>

                {/* Primary Officer & Nature of Call */}
                <div className="kst-filter-group">
                  <label className="kst-filter-label">Primary {officerLabel}</label>
                  <Dropdownlist
                    value={selectedOfficerId}
                    items={officerList}
                    labelKey="FullName"
                    valueKey="id"
                    placeholder={`All ${officerPluralLabel}`}
                    clearable={true}
                    onChange={(value) => setSelectedOfficerId(value as number | null)}
                    onOpen={closeDatePickers}
                  />
                </div>
                <div className="kst-filter-group">
                  <label className="kst-filter-label">Nature of Call</label>
                  <MultiSelect
                    value={selectedNatureOfCallCodes}
                    items={natureOfCallList}
                    labelKey="value"
                    valueKey="code"
                    groupBy={listHasOtherCompanyItems(natureOfCallList) ? 'companyName' : null}
                    groupOrder="insertion"
                    placeholder="Select Nature Of Call..."
                    onChange={setSelectedNatureOfCallCodes}
                    onOpen={closeDatePickers}
                  />
                </div>

                {/* Disposition Code */}
                <div className="kst-filter-group">
                  <label className="kst-filter-label">Disposition Code</label>
                  <MultiSelect
                    value={selectedDispositionCodes}
                    items={dispositionList}
                    labelKey="dispositionValue"
                    valueKey="dispositionCode"
                    groupBy={listHasOtherCompanyItems(dispositionList) ? 'companyName' : null}
                    groupOrder="insertion"
                    placeholder="Select Dispositions..."
                    onChange={setSelectedDispositionCodes}
                    onOpen={closeDatePickers}
                  />
                </div>

                {/* Map Areas */}
                <div className="kst-filter-group">
                  <label className="kst-filter-label">Map Areas</label>
                  <MultiSelect
                    value={selectedMapAreaIds}
                    items={mapAreaList}
                    labelKey="label"
                    valueKey="id"
                    placeholder="Select Map Areas..."
                    onChange={setSelectedMapAreaIds}
                    onOpen={closeDatePickers}
                  />
                </div>

                {/* Time Zone (companyId 2 only) */}
                {showTimeZoneFilter && (
                  <div className="kst-filter-group">
                    <label className="kst-filter-label">Time Zone</label>
                    <Dropdownlist
                      value={selectedTimeZoneId}
                      items={timeZones}
                      labelKey="value"
                      valueKey="code"
                      placeholder="Select timezone..."
                      sortItems={false}
                      onChange={(val) => setSelectedTimeZoneId(val as string)}
                      onOpen={closeDatePickers}
                    />
                  </div>
                )}

                {/* Include Checkboxes */}
                <div className="kst-filter-group kst-filter-group--full">
                  <label className="kst-filter-label">Include:</label>
                  <div className="kst-checkbox-row">
                    <label className="kst-checkbox-label" htmlFor="chkIncludeCalls">
                      <input
                        type="checkbox"
                        id="chkIncludeCalls"
                        className="kstc-checkbox"
                        checked={hasDispositionFilter || includeCalls}
                        disabled={hasDispositionFilter}
                        onChange={(e) => setIncludeCalls(e.target.checked)}
                      /> Calls
                    </label>
                    <label className="kst-checkbox-label" htmlFor="chkIncludeCaseReports">
                      <input
                        type="checkbox"
                        id="chkIncludeCaseReports"
                        className="kstc-checkbox"
                        checked={!hasDispositionFilter && includeCaseReports}
                        disabled={hasDispositionFilter}
                        onChange={(e) => setIncludeCaseReports(e.target.checked)}
                      /> Case Reports
                    </label>
                    <label className="kst-checkbox-label" htmlFor="chkIncludeNotes">
                      <input
                        type="checkbox"
                        id="chkIncludeNotes"
                        className="kstc-checkbox"
                        checked={!hasDispositionFilter && includeNotes}
                        disabled={hasDispositionFilter}
                        onChange={(e) => handleNotesCheckboxChange(e.target.checked)}
                      /> Notes
                    </label>
                    <label className="kst-checkbox-label" htmlFor="chkIncludeTourStops">
                      <input
                        type="checkbox"
                        id="chkIncludeTourStops"
                        className="kstc-checkbox"
                        checked={!hasDispositionFilter && includeTourStops}
                        disabled={hasDispositionFilter}
                        onChange={(e) => setIncludeTourStops(e.target.checked)}
                      /> Tour Stops
                    </label>
                    <label className="kst-checkbox-label" htmlFor="chkIncludeInternalNotes">
                      <input
                        type="checkbox"
                        id="chkIncludeInternalNotes"
                        className="kstc-checkbox"
                        checked={!hasDispositionFilter && includeInternalNotes}
                        disabled={hasDispositionFilter || !internalNotesEnabled}
                        onChange={(e) => setIncludeInternalNotes(e.target.checked)}
                      /> Internal Notes
                    </label>
                    {hasDispositionFilter && (
                      <span className="kst-filter-hint">Only calls are shown when filtering by disposition</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="kst-filter-actions">
                <button type="button" className="kstc-btn kstc-btn--primary" onClick={viewReport}>View Below</button>
                <button type="button" className="kstc-btn kstc-btn--primary" onClick={downloadPdf}>Download PDF</button>
                {showMileageButton && (
                  <button type="button" className="kstc-btn kstc-btn--primary" onClick={downloadMileage}>Download Mileage</button>
                )}
              </div>
            </div>

            {/* Calls For Service Grid */}
            <div className="kst-results-panel">
              <div className="kst-results-header">
                <span className="kst-results-header-title">Activity - Calls For Service</span>
                <button
                  type="button"
                  className="kst-download-btn"
                  onClick={downloadCallCardCsv}
                  disabled={!hasResults}
                  title="Download CSV"
                >
                  {downloadSvg}
                </button>
              </div>
              <div className="kst-results-body">
                <KstDataGrid
                  columns={callCardGridColumns}
                  rows={callCardGridRows}
                  pager={callCardGridPager}
                  showcheckbox={false}
                  showheader={false}
                  mobiletitlefield="eventId"
                  loading={isReportLoading}
                  onGoToPage={callCardGoToPage}
                />
              </div>
            </div>

            {/* Reports Grid */}
            <div className="kst-results-panel">
              <div className="kst-results-header">
                <span className="kst-results-header-title">Activity - Reports</span>
                <button
                  type="button"
                  className="kst-download-btn"
                  onClick={downloadCaseReportCsv}
                  disabled={!hasResults}
                  title="Download CSV"
                >
                  {downloadSvg}
                </button>
              </div>
              <div className="kst-results-body">
                <KstDataGrid
                  columns={reportGridColumns}
                  rows={reportGridRows}
                  pager={reportGridPager}
                  showcheckbox={false}
                  showheader={false}
                  mobiletitlefield="caseId"
                  loading={isReportLoading}
                  onGoToPage={reportGoToPage}
                />
              </div>
            </div>

            {/* Notes Grid */}
            <div className="kst-results-panel">
              <div className="kst-results-header">
                <span className="kst-results-header-title">Activity - Notes</span>
                <button
                  type="button"
                  className="kst-download-btn"
                  onClick={downloadNotesCsv}
                  disabled={!hasResults}
                  title="Download CSV"
                >
                  {downloadSvg}
                </button>
              </div>
              <div className="kst-results-body">
                <KstDataGrid
                  columns={notesGridColumns}
                  rows={notesGridRows}
                  pager={notesGridPager}
                  showcheckbox={false}
                  showheader={false}
                  mobiletitlefield="subject"
                  loading={isReportLoading}
                  onGoToPage={notesGoToPage}
                />
              </div>
            </div>

            {/* Tour Stops Grid */}
            <div className="kst-results-panel">
              <div className="kst-results-header">
                <span className="kst-results-header-title">Activity - Tour Stops</span>
                <button
                  type="button"
                  className="kst-download-btn"
                  onClick={downloadTourStopsCsv}
                  disabled={!hasResults}
                  title="Download CSV"
                >
                  {downloadSvg}
                </button>
              </div>
              <div className="kst-results-body">
                <KstDataGrid
                  columns={tourStopsGridColumns}
                  rows={tourStopsGridRows}
                  pager={tourStopsGridPager}
                  showcheckbox={false}
                  showheader={false}
                  mobiletitlefield="location"
                  loading={isReportLoading}
                  onGoToPage={tourStopsGoToPage}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Window Registration
// ============================================================================

// Backward compat - clear results
(window as any).clearActivityReportLastResults = () => {};
